import { useApolloClient } from "@apollo/client/react";
import { EllipsisIcon } from "lucide-react";

import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { ObjectType } from "@/objects/@types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";

export function NodeActionsPopper({
  actions,
  isOpenState,
}: {
  actions: NonNullable<ReturnType<ObjectType<any>["getActions"]>>;
  isOpenState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}) {
  const [, setIsOpen] = isOpenState;
  // An action is a click of its own, or a mutation it names - run here, as a
  // bank account's card runs its own: named and never run, "Send email" and
  // a document's "Move to" did nothing.
  const client = useApolloClient();
  return (
    <Popper
      isOpenState={isOpenState}
      placement="bottom-end"
      anchor={
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid="node-title-actions-button"
            >
              <EllipsisIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center gap-1.5">
              Actions
              <HotkeyDecorator hotkey="meta+j" />
            </div>
          </TooltipContent>
        </Tooltip>
      }
    >
      <List
        data={actions}
        size="md"
        onItemClick={(action) => {
          if (action.mutationOptions) {
            void client.mutate({
              mutation: action.mutationOptions.mutation,
              ...(action.mutationOptions.options as Record<string, unknown>),
            });
          } else {
            action.onClick?.();
          }
          setIsOpen(false);
        }}
      >
        <List.Items>
          {(action: (typeof actions)[number]) => (
            <List.Item
              value={action}
              data-testid={`node-tab-action-${action.label.toLowerCase().replace(/\s/g, "-")}`}
              className="min-w-40 justify-between"
            >
              <Typography level="body-sm">{action.label}</Typography>
              <Icon ReactIcon={action.icon} size="sm" />
            </List.Item>
          )}
        </List.Items>
      </List>
    </Popper>
  );
}
